/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FeatureCollection } from 'geojson';
import {
  containsExtent,
  createEmpty,
  extend as extendExtent,
  isEmpty as isEmptyExtent,
} from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import React, { useRef } from 'react';
import { defaultBbox as defaultBboxCore } from '@opengeoweb/core';
import type { MapPreset } from '@opengeoweb/store';
import { LayerType, generateMapId } from '@opengeoweb/webmap';
import {
  OpenLayersFeatureLayer,
  OpenLayersLayer,
  OpenLayersMapView,
  defaultLayers,
  genericOpenLayersFeatureStyle,
  OpenLayersZoomControl,
  MapControls,
  viewUtils,
} from '@opengeoweb/webmap-react';
import type {
  ClickOnFeature,
  HoverSelect,
  OpenLayersLayerProps,
} from '@opengeoweb/webmap-react';

export const EXTENT_PADDING = 50;
const WARNING_LAYER_Z_INDEX = 1000;
const PREVIEW_LAYER_Z_INDEX = WARNING_LAYER_Z_INDEX + 1;
const DEFAULT_SIZE = { height: '200px', width: '100%' };
function getGeojsonExtent(
  featureCollection: FeatureCollection,
  viewSrs: string,
  dataSrs = 'EPSG:4326', // change if GeoJSON is not lon/lat
): number[] {
  const format = new GeoJSON();
  const features = format.readFeatures(featureCollection, {
    dataProjection: dataSrs,
    featureProjection: viewSrs,
  });

  const extent = createEmpty();
  for (const feat of features) {
    const geo = feat.getGeometry();
    if (geo) {
      extendExtent(extent, geo.getExtent());
    }
  }
  return extent;
}

function getContainerSize(
  el: HTMLDivElement | null,
): [number, number] | undefined {
  const rect = el?.getBoundingClientRect();
  return rect && rect.width > 0 && rect.height > 0
    ? [Math.round(rect.width), Math.round(rect.height)]
    : undefined;
}

// Padding scales with container size (10%) so small containers keep most of their area usable,
// while large containers cap at EXTENT_PADDING.
const getSafeExtentPadding = (size: [number, number] | undefined): number[] => {
  if (!size) {
    return [EXTENT_PADDING, EXTENT_PADDING, EXTENT_PADDING, EXTENT_PADDING];
  }

  const [width, height] = size;
  const padX = Math.min(EXTENT_PADDING, Math.floor(width * 0.1));
  const padY = Math.min(EXTENT_PADDING, Math.floor(height * 0.1));
  return [padY, padX, padY, padX];
};

interface WarningsMapViewProps {
  geojsonFeature?: FeatureCollection | undefined;
  previewGeojsonFeature?: FeatureCollection | undefined;
  shouldFitPreviewIntoView?: boolean;
  defaultMapPreset?: MapPreset;
  isSnapshot?: boolean;
  size?: { height: string; width: string };
  showMapMousePosition?: boolean;
  showZoomControls?: boolean;
  clickOnFeature?: ClickOnFeature;
  hoverSelect?: HoverSelect;
}
export const WarningsMapView: React.FC<WarningsMapViewProps> = ({
  geojsonFeature,
  previewGeojsonFeature,
  shouldFitPreviewIntoView = false,
  defaultMapPreset,
  isSnapshot = false,
  size = DEFAULT_SIZE,
  showMapMousePosition = true,
  showZoomControls = true,
  clickOnFeature,
  hoverSelect,
}: WarningsMapViewProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const defaultMapPresetBbox = geojsonFeature?.features.length
    ? undefined
    : defaultBboxCore;
  const defaultBbox = defaultMapPreset?.proj || defaultMapPresetBbox;

  const defaultBaseLayer =
    defaultMapPreset?.layers?.find(
      (layer) => layer.layerType === LayerType.baseLayer,
    ) || defaultLayers.baseLayerGrey;
  const defaultOverLayer =
    defaultMapPreset?.layers?.find(
      (layer) => layer.layerType === LayerType.overLayer,
    ) || defaultLayers.overLayer;

  const mapId = React.useRef<string>(
    `WarningsMapView_${generateMapId()}`,
  ).current;

  const warningLayer: OpenLayersLayerProps = {
    id: `${mapId}geojsonfeature`,
    geojson: geojsonFeature,
    layerType: LayerType.featureLayer,
  };

  const view = React.useMemo(
    () => viewUtils.makeView(defaultBbox?.srs),
    [defaultBbox?.srs],
  );

  const viewSrs = view.getProjection().getCode();
  const geojsonExtent = React.useMemo(
    () =>
      geojsonFeature?.features?.length
        ? getGeojsonExtent(geojsonFeature, viewSrs, 'EPSG:4326')
        : undefined,
    [geojsonFeature, viewSrs],
  );

  const previewExtent = React.useMemo(() => {
    if (!previewGeojsonFeature?.features?.length) {
      return undefined;
    }

    const extent = getGeojsonExtent(
      previewGeojsonFeature,
      viewSrs,
      'EPSG:4326',
    );
    return isEmptyExtent(extent) ? undefined : extent;
  }, [previewGeojsonFeature, viewSrs]);

  const fitViewToExtent = React.useCallback((): void => {
    const pixelSize = getContainerSize(containerRef.current);
    const padding = getSafeExtentPadding(pixelSize);
    const bbox = defaultBbox?.bbox;

    if (bbox && !geojsonFeature?.features.length) {
      const extent = [bbox.left, bbox.bottom, bbox.right, bbox.top];
      // OpenLayers uses a 100x100 viewport when the map is not measured yet
      const canFitToContainer =
        !!pixelSize &&
        extent.every((value) => Number.isFinite(value)) &&
        bbox.left !== bbox.right &&
        bbox.bottom !== bbox.top;

      if (canFitToContainer) {
        view.fit(extent, { size: pixelSize, duration: 0 });
      } else {
        viewUtils.setViewFromExtent(view, bbox);
      }
      return;
    }

    if (geojsonExtent && !isEmptyExtent(geojsonExtent)) {
      view.fit(geojsonExtent, {
        size: pixelSize,
        padding,
        maxZoom: 12,
        duration: 0,
      });

      return;
    }

    // fallback
    viewUtils.setViewFromFeature(view, geojsonFeature);
  }, [defaultBbox, geojsonFeature, geojsonExtent, view]);

  const defaultBboxBounds = defaultBbox?.bbox;
  const bboxKey = defaultBboxBounds
    ? [
        defaultBboxBounds.left,
        defaultBboxBounds.bottom,
        defaultBboxBounds.right,
        defaultBboxBounds.top,
      ].join(',')
    : '';
  const extentKey = geojsonExtent ? geojsonExtent.join(',') : '';
  const previewKey = previewExtent ? previewExtent.join(',') : '';
  const fitKey = `${viewSrs}|${bboxKey}|${extentKey}`;

  const fitViewToExtentRef = React.useRef(fitViewToExtent);
  fitViewToExtentRef.current = fitViewToExtent;

  React.useEffect(() => {
    fitViewToExtentRef.current();
  }, [fitKey, view]);

  // Only ever widens: a preview already in view leaves the map untouched, and
  // the widened view stays until the map is fitted to new data
  const widenViewForPreview = React.useCallback((): void => {
    if (!previewExtent) {
      return;
    }

    const pixelSize = getContainerSize(containerRef.current);
    const currentExtent = view.calculateExtent(pixelSize);
    if (containsExtent(currentExtent, previewExtent)) {
      return;
    }

    view.fit(extendExtent(currentExtent, previewExtent), {
      size: pixelSize,
      padding: getSafeExtentPadding(pixelSize),
      duration: 0,
    });
  }, [previewExtent, view]);

  const widenViewForPreviewRef = React.useRef(widenViewForPreview);
  widenViewForPreviewRef.current = widenViewForPreview;

  React.useEffect(() => {
    if (!shouldFitPreviewIntoView) {
      return;
    }
    widenViewForPreviewRef.current();
  }, [previewKey, shouldFitPreviewIntoView, view]);

  return (
    <div ref={containerRef} style={size}>
      <OpenLayersMapView
        holdShiftToScroll={true}
        mapId={mapId}
        showMapMousePosition={showMapMousePosition}
        view={view}
      >
        {!!showZoomControls && (
          <MapControls style={{ top: 0 }}>
            <OpenLayersZoomControl homeExtent={view.calculateExtent()} />
          </MapControls>
        )}
        {!isSnapshot && (
          <OpenLayersLayer {...defaultBaseLayer} id={`${mapId}_baseLayer`} />
        )}
        {!!warningLayer.geojson && (
          <OpenLayersFeatureLayer
            clickOnFeature={clickOnFeature}
            featureCollection={warningLayer.geojson}
            hoverSelect={hoverSelect}
            style={genericOpenLayersFeatureStyle}
            zIndex={WARNING_LAYER_Z_INDEX}
          />
        )}
        {!!previewGeojsonFeature?.features.length && (
          <OpenLayersFeatureLayer
            featureCollection={previewGeojsonFeature}
            style={genericOpenLayersFeatureStyle}
            zIndex={PREVIEW_LAYER_Z_INDEX}
          />
        )}
        {!isSnapshot && (
          <OpenLayersLayer {...defaultOverLayer} id={`${mapId}_overlayer`} />
        )}
      </OpenLayersMapView>
    </div>
  );
};
